// ProductInfoCard.tsx
import { Box, Card, CardContent, Divider, Grid, Stack, Typography } from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { OrganizationTableItem } from 'types'

type MaybeNum = number | string | null | undefined

// Narrow shape for what we actually render.
// If you already have a Product type, you can intersect or map into this.
export type ProductInfo = {
  productName?: string
  status?: string
  category?: string
  subcategory?: string
  accountUsed?: string
  qty?: MaybeNum
  price?: MaybeNum
  billedPrice?: MaybeNum
  margin?: MaybeNum
  grossProfit?: MaybeNum
  startDate?: string | Date | null
  endDate?: string | Date | null
  dateRange?: [string, string] | []
  selectedProduct?: {
    title?: string
    status?: string
    category_name?: string
    subcategory?: string
    accountUsed?: string
    qty?: MaybeNum
    price?: MaybeNum
    billedPrice?: MaybeNum
    margin?: MaybeNum
    grossProfit?: MaybeNum
    startDate?: string | Date | null
    endDate?: string | Date | null
  }
}

function toNumber(n: MaybeNum): number | undefined {
  if (n === null || n === undefined) return
  const num = typeof n === 'string' ? Number(n) : n
  return Number.isFinite(num) ? num : undefined
}

const usd = (n?: MaybeNum) =>
  toNumber(n) !== undefined
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(
        toNumber(n)!
      )
    : '—'

const pct = (n?: MaybeNum) => (toNumber(n) !== undefined ? `${toNumber(n)!.toFixed(0)}%` : '—')

const qtyFmt = (n?: MaybeNum) => (toNumber(n) !== undefined ? String(toNumber(n)) : '—')

const dateFmt = (d?: string | Date | null) => {
  if (!d) return '—'
  const dt = typeof d === 'string' ? new Date(d) : d
  return isNaN(dt.getTime())
    ? '—'
    : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

type Props = {
  item: any // your raw item
  titleFallback?: string
}

export default function ProductInfoCard({ item }: { item?: OrganizationTableItem }) {
  const { PK, SK } = useParams()
  const { pinned } = useLayoutContext()
  console.log('item', item)
  // Map your raw object into the narrow render shape
  const mapped: ProductInfo = {
    productName: item?.productName || item?.selectedProduct?.title || 'No Product Name',
    status: item?.status,
    category: item?.category || item?.selectedProduct?.category_name,
    subcategory: item?.subcategory || item?.selectedProduct?.subcategory,
    accountUsed: item?.accountUsed,
    qty: item?.qty ?? item?.selectedProduct?.qty,
    price: item?.price ?? item?.selectedProduct?.price,
    billedPrice: item?.billedPrice,
    margin: item?.margin ?? item?.selectedProduct?.margin,
    grossProfit: item?.grossProfit,
    startDate: item?.startDate ?? item?.dateRange?.[0],
    endDate: item?.endDate ?? item?.dateRange?.[1],
  }

  return (
    <Box
      sx={{
        position: 'relative',
        transition: 'all 0.3s ease-in-out',
        height: '100%',
      }}
    >
      <Card variant="outlined" sx={{ minWidth: 320 }}>
        <CardContent>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={1}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {mapped.category || '—'} {mapped.subcategory ? `• ${mapped.subcategory}` : ''}
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {mapped.productName || 'Product'}
              </Typography>
            </Box>
            {mapped.status && (
              <Typography sx={{ color: renderColorByString(mapped.status) }}>{mapped.status}</Typography>
            )}
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {/* Money and qty row */}
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Field label="Price" value={usd(mapped.price)} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Billed" value={usd(mapped.billedPrice)} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Gross Profit" value={usd(mapped.grossProfit)} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Margin" value={pct(mapped.margin)} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Quantity" value={qtyFmt(mapped.qty)} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Account Used" value={mapped.accountUsed || '—'} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* Dates */}
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Field label="Start" value={dateFmt(mapped.startDate)} />
            </Grid>
            <Grid item xs={6}>
              <Field label="End" value={dateFmt(mapped.endDate)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Box>
  )
}
